import React, { useState } from 'react';
import Header from '../../components/common/global-top-bar';
import SwitchBar from '../../components/switch-bar';
import Spinner from '../../components/common/spinner';
import { PERSONA_TABS } from './persona-constants';
import RuleEditorDrawer from './rule-editor';
import {
  Rule,
  RulePayload,
  useCreateSharedRule,
  useDeleteRule,
  useSharedRules,
  useUpdateRule,
} from '../../hooks/usePersonaAdmin';

const SharedRules: React.FC = () => {
  const { data, isLoading } = useSharedRules(true);
  const createSharedRule = useCreateSharedRule();
  const updateRule = useUpdateRule();
  const deleteRule = useDeleteRule();

  const [editing, setEditing] = useState<Rule | 'new' | null>(null);

  const rules = data?.data || [];

  const openNew = () => setEditing('new');
  const openEdit = (rule: Rule) => setEditing(rule);
  const close = () => setEditing(null);

  const handleSubmit = async (payload: RulePayload) => {
    if (editing === 'new') {
      await createSharedRule.mutateAsync(payload);
    } else if (editing) {
      await updateRule.mutateAsync({ ruleId: editing.id, payload });
    }
    close();
  };

  const handleDelete = async () => {
    if (editing && editing !== 'new') {
      await deleteRule.mutateAsync(editing.id);
      close();
    }
  };

  const toggleEnabled = (rule: Rule) => {
    updateRule.mutate({ ruleId: rule.id, payload: { enabled: !rule.enabled } });
  };

  return (
    <div>
      <Header heading="Personas" />
      <SwitchBar heading={PERSONA_TABS} position={1} />

      <div className="mx-auto max-w-5xl space-y-4 p-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Shared Rules</h2>
            <p className="text-xs text-gray-500">
              Shared rules are evaluated against every persona. Use them for cross-persona guarantees like crisis
              routing.
            </p>
          </div>
          <button
            className="bg-blue-primary rounded px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            onClick={openNew}
          >
            New Shared Rule
          </button>
        </div>

        {isLoading && <Spinner alignmentClass="flex items-center justify-center py-16" />}

        {!isLoading && (
          <div className="rounded-md border border-gray-200 bg-white shadow-sm">
            <table className="w-full table-fixed border-collapse text-sm">
              <thead className="bg-gray-50">
                <tr className="border-b text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <th className="py-2 pl-4">Slug / Notes</th>
                  <th className="w-64 py-2">Tags</th>
                  <th className="w-20 py-2">Turns</th>
                  <th className="w-24 py-2">Enabled</th>
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-xs text-gray-400">
                      No shared rules yet.
                    </td>
                  </tr>
                )}
                {rules.map((rule) => (
                  <tr key={rule.id} className="cursor-pointer border-b hover:bg-gray-50" onClick={() => openEdit(rule)}>
                    <td className="py-3 pl-4 pr-2">
                      <div className="font-medium text-gray-900">{rule.slug}</div>
                      {rule.notes && <div className="max-w-md truncate text-xs text-gray-500">{rule.notes}</div>}
                    </td>
                    <td className="py-3 pr-2 text-xs text-gray-600">
                      {(rule.tags || []).slice(0, 4).map((tag) => (
                        <span
                          key={tag}
                          className="mr-1 inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                        >
                          {tag}
                        </span>
                      ))}
                    </td>
                    <td className="py-3 pr-2 text-xs text-gray-600">{rule.turns?.length ?? 0}</td>
                    <td className="py-3 pr-2" onClick={(e) => e.stopPropagation()}>
                      <label className="relative inline-flex cursor-pointer items-center">
                        <input
                          type="checkbox"
                          className="peer sr-only"
                          checked={rule.enabled}
                          onChange={() => toggleEnabled(rule)}
                        />
                        <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-800 peer-checked:after:translate-x-full peer-checked:after:border-white" />
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <RuleEditorDrawer
          rule={editing === 'new' ? null : editing}
          sharedOnly
          onClose={close}
          onSubmit={handleSubmit}
          onDelete={editing !== 'new' ? handleDelete : undefined}
          isSubmitting={createSharedRule.isLoading || updateRule.isLoading}
          isDeleting={deleteRule.isLoading}
          title={editing === 'new' ? 'New Shared Rule' : `Edit Shared Rule · ${(editing as Rule).slug}`}
        />
      )}
    </div>
  );
};

export default SharedRules;
